/**
 * line-ruler.js — геометрия линейки для чтения, которая стоит на СТРОКАХ текста.
 *
 * ── Зачем ───────────────────────────────────────────────────────────────────
 * Линейка в /reading-ruler и /dyslexia-reader ставила полосу по центру
 * указателя, высотой в пикселях: в /reading-ruler — отдельный ползунок, никак
 * не связанный с размером текста, в /dyslexia-reader — одна высота строки, а
 * «окно» — 2,5 строки. Полоса почти всегда резала строку пополам. Замер на
 * странице /reading-ruler (16.10.2026, указатель прошёл по тексту с шагом
 * 2 px): одна строка видна целиком лишь в 47 % положений при настройках по
 * умолчанию, в 18 % при тексте 24 px, в 9 % при 28 px и в 3 % при 14 px;
 * подчёркивание перечёркивало буквы в половине положений. Человек, которому
 * линейка нужна, чтобы не терять строку, получал полосу между двумя строками.
 *
 * ── Модель ──────────────────────────────────────────────────────────────────
 * Строка k текстового блока занимает [top + k·pitch, top + (k+1)·pitch], где
 * pitch — вычисленный line-height блока. Это верно, когда все строки одной
 * высоты, и ОБЯЗАННОСТЬ вызывающего — это обеспечить:
 *   · line-height задан ЧИСЛОМ на самом блоке (не 'normal');
 *   · у вложенных элементов с другим начертанием (жирная половина слова,
 *     другой шрифт) — line-height:0, иначе такой элемент раздувает строку:
 *     полужирный OpenDyslexic / Lexend делал каждую строку на 1 px выше, и
 *     линейка уходила на строку за каждые 40.
 * Замер после этого: отклонение центра каждой строки от модели — 0 px для
 * системного шрифта, OpenDyslexic, Comic, Lexend и Atkinson (с кириллицей из
 * запасного шрифта), с бионическим чтением и без.
 *
 * Полоса — всегда целые строки: n строк вокруг выбранной, окно не выходит за
 * текст. Подчёркивание — под буквами строки, посередине промежутка до
 * следующей: не задевает ни эту строку, ни следующую.
 *
 * Сторожа — tests/js/line-ruler.test.mjs (модель), LineRulerHasOneOwnerTest
 * (оба тула берут геометрию отсюда). Разбор — docs/why/reading-ruler.md.
 */
(function () {
'use strict';

if (window.LineRuler) return;

function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

/**
 * Геометрия строк.
 * @param {Element} textEl   блок с текстом (line-height — числом)
 * @param {Element} frameEl  позиционированный предок, в координатах которого
 *                           стоят элементы линейки (position:absolute)
 * @param {Element} probeEl  любое слово текста — по нему высота самих букв;
 *                           нет слова — нет строк (rows: 0)
 * @returns {{pitch:number, top:number, rows:number, ink:number}}
 */
function measure(textEl, frameEl, probeEl) {
    var cs = getComputedStyle(textEl);
    var fs = parseFloat(cs.fontSize) || 16;
    var pitch = parseFloat(cs.lineHeight);
    if (!(pitch > 0)) pitch = fs * 1.2;
    var tr = textEl.getBoundingClientRect(), fr = frameEl.getBoundingClientRect();
    var padT = parseFloat(cs.paddingTop) || 0, padB = parseFloat(cs.paddingBottom) || 0;
    var bT = parseFloat(cs.borderTopWidth) || 0;
    var top = tr.top - fr.top - frameEl.clientTop + bT + padT;
    var rows = probeEl ? Math.max(1, Math.round((textEl.clientHeight - padT - padB) / pitch)) : 0;
    var ink = probeEl ? probeEl.getBoundingClientRect().height : fs * 1.2;
    return { pitch: pitch, top: top, rows: rows, ink: ink };
}

/** Строка под точкой clientY (координата окна), прижатая к тексту; -1 — строк нет. */
function rowAt(g, frameEl, clientY) {
    if (!g || !g.rows) return -1;
    var fr = frameEl.getBoundingClientRect();
    var y = clientY - fr.top - frameEl.clientTop;
    return clamp(Math.floor((y - g.top) / g.pitch), 0, g.rows - 1);
}

/** Строка, на которой стоит элемент (его первая строка, если он переносится). */
function rowOf(g, frameEl, el) {
    if (!el) return -1;
    var r = (el.getClientRects && el.getClientRects()[0]) || el.getBoundingClientRect();
    return rowAt(g, frameEl, r.top + r.height / 2);
}

/**
 * Полоса из n целых строк вокруг строки row, не выходящая за текст.
 * @returns {{first:number, last:number, top:number, bottom:number}}
 */
function span(g, row, n) {
    n = Math.max(1, Math.min(n || 1, g.rows || 1));
    var first = clamp(row - Math.floor((n - 1) / 2), 0, Math.max(0, g.rows - n));
    return { first: first, last: first + n - 1, top: g.top + first * g.pitch, bottom: g.top + (first + n) * g.pitch };
}

/** Верх черты подчёркивания толщиной thickness под строкой row. */
function underlineTop(g, row, thickness) {
    var inkBottom = g.top + row * g.pitch + g.pitch / 2 + g.ink / 2;
    return inkBottom + Math.max(0, (g.pitch - g.ink - (thickness || 3)) / 2);
}

/**
 * Первое из упорядоченных по тексту слов, стоящее на строке row или ниже
 * (на пустой строке между абзацами слов нет), — двоичным поиском: книга
 * бывает в сотню тысяч слов. -1 — ниже row слов нет.
 */
function firstFrom(g, frameEl, els, row) {
    var lo = 0, hi = els.length - 1, ans = -1;
    while (lo <= hi) {
        var mid = (lo + hi) >> 1;
        if (rowOf(g, frameEl, els[mid]) >= row) { ans = mid; hi = mid - 1; }
        else lo = mid + 1;
    }
    return ans;
}

window.LineRuler = {
    measure: measure,
    rowAt: rowAt,
    rowOf: rowOf,
    span: span,
    underlineTop: underlineTop,
    firstFrom: firstFrom
};
})();
